// Physics.js - Handles game physics, collisions and movement

class Physics {
  constructor(map, tileSize) {
    this.map = map;
    this.tileSize = tileSize;
  }

  movePacman(pacman, ghost, foods) {
    // Handle buffered direction changes for smoother controls
    if (pacman.nextDirection && this.canChangeDirection(pacman, pacman.nextDirection)) {
      pacman.direction = pacman.nextDirection;
      pacman.nextDirection = null;
    }

    const { direction, speed } = pacman;

    // Calculate new position based on direction
    let newX = pacman.x + (direction === 'right' ? speed : direction === 'left' ? -speed : 0);
    let newY = pacman.y + (direction === 'down' ? speed : direction === 'up' ? -speed : 0);

    // Try to move in the desired direction
    if (this.canMove(newX, newY)) {
      pacman.x = newX;
      pacman.y = newY;
    } else {
      // If direct movement fails, try to slide along walls (smooth cornering)
      // This helps with navigation in tight spaces
      let slideX = pacman.x;
      let slideY = pacman.y;

      // Try horizontal movement only
      if (direction === 'left' || direction === 'right') {
        slideX = newX;
        slideY = pacman.y;
        if (this.canMove(slideX, slideY)) {
          pacman.x = slideX;
          // Allow some vertical adjustment to prevent getting stuck
          this.adjustVerticalPosition(pacman);
        }
      }
      // Try vertical movement only
      else if (direction === 'up' || direction === 'down') {
        slideX = pacman.x;
        slideY = newY;
        if (this.canMove(slideX, slideY)) {
          pacman.y = slideY;
          // Allow some horizontal adjustment to prevent getting stuck
          this.adjustHorizontalPosition(pacman);
        }
      }
    }

    // Animate mouth
    pacman.mouthAngle += pacman.mouthOpening ? 0.1 : -0.1;
    if (pacman.mouthAngle >= 0.5) {
      pacman.mouthOpening = false;
    } else if (pacman.mouthAngle <= 0) {
      pacman.mouthOpening = true;
    }

    // Handle power mode
    if (pacman.isPowered) {
      pacman.powerTimer--;
      if (pacman.powerTimer <= 0) {
        pacman.isPowered = false;
        pacman.speed = 0.1; // Reset to default speed
        ghost.color = 'red';
      }
    }

    this.checkFoodCollision(pacman, ghost, foods);
  }

  // Check if Pacman can change direction (when aligned with grid)
  canChangeDirection(pacman, newDirection) {
    // Calculate new position in the desired direction
    const testSpeed = 0.08; // Small test movement
    let testX = pacman.x;
    let testY = pacman.y;

    if (newDirection === 'right') testX += testSpeed;
    else if (newDirection === 'left') testX -= testSpeed;
    else if (newDirection === 'down') testY += testSpeed;
    else if (newDirection === 'up') testY -= testSpeed;

    return this.canMove(testX, testY);
  }

  // Helper methods to prevent getting stuck in walls
  adjustVerticalPosition(pacman) {
    // Try small vertical adjustments to free Pacman from walls
    const adjustments = [-0.05, 0.05, -0.1, 0.1];
    for (const adj of adjustments) {
      const testY = pacman.y + adj;
      if (this.canMove(pacman.x, testY)) {
        pacman.y = testY;
        break;
      }
    }
  }

  adjustHorizontalPosition(pacman) {
    // Try small horizontal adjustments to free Pacman from walls
    const adjustments = [-0.05, 0.05, -0.1, 0.1];
    for (const adj of adjustments) {
      const testX = pacman.x + adj;
      if (this.canMove(testX, pacman.y)) {
        pacman.x = testX;
        break;
      }
    }
  }

  moveGhost(ghost, pacman) {
    const dx = pacman.x - ghost.x;
    const dy = pacman.y - ghost.y;
    const { speed } = ghost;

    // Ghost movement logic - chase or flee based on power mode
    const directionMultiplier = pacman.isPowered ? -1 : 1;
    let newX, newY;

    if (Math.abs(dx) > Math.abs(dy)) {
      newX = ghost.x + Math.sign(dx) * speed * directionMultiplier;
      newY = ghost.y;
    } else {
      newX = ghost.x;
      newY = ghost.y + Math.sign(dy) * speed * directionMultiplier;
    }

    // Try to move in the desired direction
    if (this.canMove(newX, newY)) {
      ghost.x = newX;
      ghost.y = newY;
    } else {
      // If direct movement fails, try alternative movement
      // Ghosts can also benefit from smoother movement in tight spaces
      if (Math.abs(dx) > Math.abs(dy)) {
        // Try vertical movement if horizontal fails
        newX = ghost.x;
        newY = ghost.y + Math.sign(dy) * speed * directionMultiplier;
        if (this.canMove(newX, newY)) {
          ghost.x = newX;
          ghost.y = newY;
        }
      } else {
        // Try horizontal movement if vertical fails
        newX = ghost.x + Math.sign(dx) * speed * directionMultiplier;
        newY = ghost.y;
        if (this.canMove(newX, newY)) {
          ghost.x = newX;
          ghost.y = newY;
        }
      }
    }
  }

  canMove(x, y) {
    // Use a smaller collision box for smoother movement in narrow passages
    // Pacman should be able to fit through 1-tile wide passages
    const collisionSize = 0.6; // Reduced from 0.9 to allow better passage through narrow areas

    // Check collision points around Pacman's center
    const collisionPoints = [
      [x + 0.5 - collisionSize/2, y + 0.5 - collisionSize/2], // Top-left
      [x + 0.5 + collisionSize/2, y + 0.5 - collisionSize/2], // Top-right
      [x + 0.5 - collisionSize/2, y + 0.5 + collisionSize/2], // Bottom-left
      [x + 0.5 + collisionSize/2, y + 0.5 + collisionSize/2], // Bottom-right
      [x + 0.5, y + 0.5] // Center point
    ];

    return collisionPoints.every(([cx, cy]) => !this.isWall(cx, cy));
  }

  isWall(x, y) {
    const gridX = Math.floor(x);
    const gridY = Math.floor(y);
    // Check if position is out of bounds or is a wall
    return gridX < 0 || gridX >= this.map[0].length ||
        gridY < 0 || gridY >= this.map.length ||
        this.map[gridY][gridX] === 1;
  }

  checkCollision(pacman, ghost) {
    // Check if pacman and ghost are close enough to collide
    const dx = Math.abs(pacman.x - ghost.x);
    const dy = Math.abs(pacman.y - ghost.y);
    return dx < 0.5 && dy < 0.5;
  }

  checkFoodCollision(pacman, ghost, foods) {
    Object.values(foods).forEach(food => {
      if (food.eaten) return;

      // Increased collision radius to make it easier to eat food
      const dx = Math.abs(pacman.x - food.x);
      const dy = Math.abs(pacman.y - food.y);
      const collisionThreshold = 0.8; // Increased from 0.5

      if (dx < collisionThreshold && dy < collisionThreshold) {
        // Mark food as eaten
        food.eaten = true;

        // Apply effects based on food type
        if (food.type === 'dot') {
          // Dots just give points, no power effects
          // Points will be added in the main game loop via checkFoodStatus
        } else {
          // Special foods activate power mode
          pacman.isPowered = true;
          ghost.color = 'blue';

          // Apply different effects based on food type
          switch(food.type) {
            case 'fruit':
              pacman.powerTimer = 600; // 10 seconds power
              break;
            case 'burger':
              pacman.powerTimer = 300; // 5 seconds power
              pacman.speed = 0.15; // Speed boost
              break;
            case 'pizza':
              pacman.powerTimer = 900; // 15 seconds power
              break;
          }
        }
      }
    });
  }
}

export default Physics; 